import { extractSqlOps } from "./index.js";
import { collectWindowOps, groupActivity, distinctKey } from "../../src/build/index.js";
import { describeState, reconstructState } from "../../src/build/reconstruct.js";

export const extract = extractSqlOps;
const RANK = { answered: 3, errored: 2, denied: 2, unrecorded: 1 };
export const informedness = (op) => (RANK[op.outcome] || 0) * 10000 + (op.output?.length || 0);
export const collect = (prompts) => collectWindowOps(prompts, { informedness });

// The TABLE is the unit, not the query. Two agents asking different questions of
// `run_report` are both telling you about `run_report`.
export const groupBy = (op) => (op.tables?.length
  ? [op.database, op.tables[0]].filter(Boolean).join(".")
  : (op.database || distinctKey("unnamed table", op)));

// A row count is platform schema — a count is a count on every database — so it
// accumulates. What a `SELECT id, state` returned does not, and is left alone.
export const stateOf = (flat) => reconstructState(flat, {
  target: groupBy,
  at: (op) => op.ts,
  read: (op) => (op.rows == null ? null : { rows: op.rows }),
  change: (op) => (op.verb === "read" ? null : `${op.verb}: ${op.sql.slice(0, 60)}`),
});

export const build = (flat) => {
  const recon = stateOf(flat);
  const byTarget = new Map(recon.targets.map((t) => [t.target, t]));
  const built = grouped(flat);
  for (const g of built.groups) {
    const entry = byTarget.get(g.key);
    if (!entry) continue;
    g.state = describeState(entry, { fields: ["rows"] });
    g.changes = entry.changes.map((c) => ({ what: c.what, at: c.at, checked: c.checked, stateDiffered: c.stateDiffered }));
    g.unchecked = entry.unchecked;
    if (entry.unchecked) g.labels = [`${entry.unchecked} unverified write${entry.unchecked > 1 ? "s" : ""}`, ...g.labels];
    if (g.state) g.labels = [g.state.split(" — ")[0], ...g.labels];
  }
  return built;
};

const grouped = (flat) => groupActivity(flat, {
  key: groupBy,
  label: (op) => (op.tables?.length ? [op.database, op.tables[0]].filter(Boolean).join(".") : (op.database || "an unnamed table")),
  seed: () => ({ kinds: {}, outcomes: {}, labels: [], hosts: [], joined: [] }),
  fold: (g, op) => {
    g.kinds[op.verb] = (g.kinds[op.verb] || 0) + 1;
    g.outcomes[op.outcome] = (g.outcomes[op.outcome] || 0) + 1;
    if (op.host && !g.hosts.includes(op.host)) g.hosts.push(op.host);
    for (const t of (op.tables || []).slice(1)) if (!g.joined.includes(t)) g.joined.push(t);
  },
  finish: (g) => { g.labels = [...g.hosts.slice(0, 1), ...g.joined.slice(0, 3).map((t) => `+ ${t}`)]; },
});

export const anchor = () => "select";
export const ignore = ["output", "sql", "steps", "checks"];
